import { useCallback, useMemo } from 'react'
import { api } from '../api/client'
import type { Exchange } from '../api/client'
import { useItemQuery } from './useItemQuery'
import type { MultiSelectOption } from '../components/common/MultiSelect'

/**
 * Die Handelsplätze als Auswahlliste, mit ihren Favoriten.
 *
 * Sieben Seiten haben mit Handelsplätzen zu tun, und jede, die einen Filter
 * darauf anbietet, bräuchte sonst dasselbe dreimal: den Abruf, die Abbildung
 * auf eine Auswahlliste und das Herausziehen der Favoriten. Dreimal
 * geschrieben wird daraus dreimal ein bisschen anders — die eine Seite zeigt
 * das Kürzel, die nächste den MIC, die dritte lädt hundert Zeilen statt aller.
 *
 * WOHER die Plätze kommen, ist Sache dieses Hooks und nicht der Seiten. Sie
 * kennen nur das Ergebnis. Wenn der Abruf je Seite später stört — beim
 * Blättern durch die Navigation lädt jede neu —, wird daraus ein Provider im
 * AppLayout, wie ihn `SessionContext` schon vormacht, und keine einzige Seite
 * ändert sich dabei.
 */
export interface ExchangeOptions {
  /** Für die Mehrfachauswahl: Kürzel als Beschriftung, MIC als Wert. */
  options: MultiSelectOption[]
  /** Die MIC der Favoriten, in der Reihenfolge der Liste. */
  favoriteCodes: string[]
  /** Alle Plätze, falls eine Seite mehr braucht als die Auswahlliste. */
  exchanges: Exchange[]
  /**
   * Sind die Plätze da?
   *
   * Wichtig für Seiten, die auf der Vorauswahl aufbauen: Ihre eigene Abfrage
   * sollte warten. Sonst läuft die erste ungefiltert über den ganzen Bestand
   * und wird eine Zehntelsekunde später von der gefilterten abgelöst.
   */
  ready: boolean
}

export function useExchangeOptions(enabled: boolean = true): ExchangeOptions {
  // `useCallback` ist hier nicht schmückend: Die Ladefunktion steht in den
  // Abhängigkeiten des Effekts. Eine bei jedem Rendern neu gebildete Funktion
  // löst ihn erneut aus, das Ergebnis setzt den Zustand, das rendert neu — und
  // der Abruf läuft endlos, bis der Server mit 429 abriegelt.
  const laden = useCallback(
    (signal: AbortSignal) => api.exchanges({ size: 200, sort: 'code' }, signal),
    [])
  const { item } = useItemQuery(laden, enabled)

  const exchanges = useMemo(() => item?.items ?? [], [item])

  const options = useMemo<MultiSelectOption[]>(
    // Beschriftung ist das gebräuchliche Kürzel, der Wert der MIC: Gesucht
    // wird nach NASDAQ, gespeichert und verknüpft wird XNAS. Fehlt das
    // Kürzel, tritt der MIC an seine Stelle — eine leere Zeile in der Liste
    // wäre schlimmer als eine kryptische.
    () => exchanges.map((e) => ({
      value: e.code,
      label: e.alias || e.code,
      hint: e.name,
    })),
    [exchanges])

  const favoriteCodes = useMemo(
    () => exchanges.filter((e) => e.favorite).map((e) => e.code),
    [exchanges])

  return { options, favoriteCodes, exchanges, ready: Boolean(item) }
}
